import {
  getApiAccountsV1UserRetrieveQueryKey,
  useApiAccountsV1SignoutCreate,
  useApiAccountsV1UserPartialUpdate,
  useCurrentUser,
  useQueryClient,
} from 'api';
import { NATIVE_LANGUAGE_DISPLAY_NAMES, SUPPORTED_I18N_LANGUAGES, useTranslation } from 'i18n';
import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from 'ui';
import { Check, Logout, PersonOutlined, Settings } from 'ui/icons';

import { useNotificationPreferenceToggles } from '../../hooks/useNotificationPreferenceToggles/useNotificationPreferenceToggles';

export const UserMenu = () => {
  const { t, language: currentLanguage } = useTranslation('features');
  const { data: user, isLoading } = useCurrentUser();
  const queryClient = useQueryClient();
  const { mutate: logOut } = useApiAccountsV1SignoutCreate({
    mutation: {
      onSuccess: () => {
        window.location.href = '/accounts/auth/sign-in/';
      },
    },
  });
  const { mutate: updateLanguage } = useApiAccountsV1UserPartialUpdate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getApiAccountsV1UserRetrieveQueryKey() });
      },
    },
  });
  const {
    emailEnabled,
    toggleEmailEnabled,
    toggles: notificationToggles,
    isLoading: isNotificationPreferenceLoading,
  } = useNotificationPreferenceToggles();
  const isNotificationPreferenceToggleDisabled = isNotificationPreferenceLoading;

  return (
    <Menu
      anchorOrigin={{
        horizontal: 'left',
        vertical: 'bottom',
      }}
      disableScrollLock
      transformOrigin={{
        horizontal: 'right',
        vertical: 'top',
      }}
      equalMenuWidth={false}
      button={
        <IconButton size="small" color="default">
          <PersonOutlined />
        </IconButton>
      }
    >
      <Stack
        sx={(theme) => ({
          borderBottom: `1px solid ${theme.palette.border.main}`,
          px: 2,
          py: 1,
        })}
      >
        {isLoading || isNotificationPreferenceToggleDisabled ? (
          <>
            <Skeleton variant="text" />
            <Skeleton variant="text" />
          </>
        ) : (
          <>
            <Typography variant="body1">{user?.full_name}</Typography>
            <Typography variant="body2" color="secondary">
              {user?.company?.trade_company.name}
            </Typography>
          </>
        )}
      </Stack>
      <MenuItem component="a" href="/accounts/manage_company/">
        <ListItemIcon>
          <Settings fontSize="small" />
        </ListItemIcon>
        <ListItemText>{t('navigation.userMenu.manageCompany')}</ListItemText>
      </MenuItem>

      <Stack sx={{ borderTop: (theme) => `1px solid ${theme.palette.border.main}`, px: 2, py: 1 }}>
        <Typography variant="caption" color="secondary">
          {t('navigation.userMenu.notifications')}
        </Typography>
      </Stack>
      <MenuItem
        disabled={isNotificationPreferenceToggleDisabled}
        onClick={() => {
          if (!isNotificationPreferenceToggleDisabled) {
            toggleEmailEnabled(!emailEnabled);
          }
        }}
      >
        <Stack
          onClick={(event) => {
            event.stopPropagation();
          }}
          onMouseDown={(event) => {
            event.stopPropagation();
          }}
        >
          <Switch
            checked={emailEnabled}
            disabled={isNotificationPreferenceToggleDisabled}
            label={t('navigation.userMenu.emailNotificationsEnabled')}
            onChange={(event) => {
              toggleEmailEnabled(event.target.checked);
            }}
          />
        </Stack>
      </MenuItem>
      {emailEnabled
        ? notificationToggles.map(({ labelKey, checked, onToggle }) => (
            <MenuItem
              key={labelKey}
              disabled={isNotificationPreferenceToggleDisabled}
              onClick={() => {
                if (!isNotificationPreferenceToggleDisabled) {
                  onToggle(!checked);
                }
              }}
            >
              <Stack
                onClick={(event) => {
                  event.stopPropagation();
                }}
                onMouseDown={(event) => {
                  event.stopPropagation();
                }}
              >
                <Switch
                  checked={checked}
                  disabled={isNotificationPreferenceToggleDisabled}
                  label={t(labelKey)}
                  onChange={(event) => {
                    onToggle(event.target.checked);
                  }}
                />
              </Stack>
            </MenuItem>
          ))
        : null}
      <Stack sx={{ borderTop: (theme) => `1px solid ${theme.palette.border.main}`, px: 2, py: 1 }}>
        <Typography variant="caption" color="secondary">
          {t('navigation.userMenu.language')}
        </Typography>
      </Stack>
      {SUPPORTED_I18N_LANGUAGES.map((lang) => (
        <MenuItem
          key={lang}
          selected={currentLanguage === lang}
          onClick={() => {
            if (currentLanguage !== lang) {
              updateLanguage({ data: { language: lang } });
            }
          }}
        >
          <ListItemIcon>{currentLanguage === lang ? <Check fontSize="small" /> : null}</ListItemIcon>
          <ListItemText>{NATIVE_LANGUAGE_DISPLAY_NAMES[lang]}</ListItemText>
        </MenuItem>
      ))}
      <Divider />
      <MenuItem
        onClick={() => {
          logOut();
        }}
      >
        <ListItemIcon>
          <Logout />
        </ListItemIcon>
        <ListItemText>{t('navigation.userMenu.logOut')}</ListItemText>
      </MenuItem>
    </Menu>
  );
};
